(ns minorodata.components.github-link-modal.core
  "Small self-scoped modal for the current user to link/unlink their own
  GitHub identity. Triggered from the sidebar user drop-up menu. Reuses the
  ui.dialog primitive (same one used for the small dialogs in the SQL console)."
  (:require
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.vcs.api :as vcs.api]
   ;; required for side-effect: registers :vcs.subs/user-link-status sub
   [minorodata.vcs.subs]
   [re-posh.core :as re-posh]
   [reagent.core :as reagent]
   [react :as react]
   ["lucide-react" :refer [GithubIcon]]
   [ui.button :refer [button]]
   [ui.dialog :refer [dialog-content dialog-description
                      dialog-root dialog-title]]
   [ui.utils :refer [clsx]]
   [ui.card :as card]))

;; Local open state. Single modal instance is mounted in the sidebar.
(defonce open?-state (reagent/atom false))

(defn open-github-link-modal
  "Opens the modal and refetches the current user's GitHub link status."
  []
  (dispatch [:vcs.api/get-user-link-status])
  (reset! open?-state true))

(defn close-github-link-modal []
  (reset! open?-state false))

(defn github-link-modal []
  (let [status     @(re-posh/subscribe [:vcs.subs/user-link-status])
        connected? (:vcs-user-link/connected status)
        login      (:vcs-user-link/login status)
        loading?        @(re-posh/subscribe [:mold-http/loading? :vcs.api/get-user-link-status])
        link-loading?   @(re-posh/subscribe [:mold-http/loading? :vcs.api/link-github-user])
        unlink-loading? @(re-posh/subscribe [:mold-http/loading? :vcs.api/unlink-github-user])]

    ;; The OAuth popup writes localStorage[window-name] and self-closes; refetch
    ;; status when that storage event fires.
    (react/useEffect
     (fn []
       (let [handler (fn [evt]
                       (when (and (= vcs.api/github-link-window-name (.-key evt))
                                  (some? (.-newValue evt)))
                         (js/localStorage.removeItem vcs.api/github-link-window-name)
                         (dispatch [:vcs.api/get-user-link-status])))]
         (.addEventListener js/window "storage" handler)
         (fn [] (.removeEventListener js/window "storage" handler))))
     #js [])

    [dialog-root {:open           @open?-state
                  :on-open-change (fn [open?] (reset! open?-state (boolean open?)))}
     [dialog-content {:size :small}
      [dialog-title "Link your GitHub account"]
      [dialog-description {:class "text-kleene-text-secondary"}
       "Connect your GitHub identity so commits and pull requests are attributed to you."]
      [card/card {:class "mt-6 overflow-hidden"}
       [card/card-content {:class "p-0"}
        [card/card-header {:class "flex items-center gap-3 p-4"}
         ;; GitHub avatar with a connection-status dot in the corner.
         [:div {:class "relative shrink-0"}
          [:div {:class "flex h-12 w-12 items-center justify-center rounded-full bg-kleene-neutral-05 text-kleene-text dark:bg-kleene-dark-03"}
           [:> GithubIcon {:size "26"}]]
          [:span {:class (clsx "absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-kleene-background"
                               (cond
                                 connected? "bg-kleene-green"
                                 loading?   "bg-kleene-neutral-02 animate-pulse"
                                 :else      "bg-kleene-neutral-02"))}]]
         [:div {:class "min-w-0 flex-1"}
          [card/card-title {:class "truncate"}
           (cond
             loading?   "Checking status…"
             connected? "Connected to GitHub"
             :else      "Not connected")]
          [card/card-description {:class "mt-0.5 truncate"}
           (cond
             loading?
             "Fetching your GitHub link status."

             (and connected? login)
             [:span "Signed in as "
              [:a {:href   (str "https://github.com/" login)
                   :target "_blank"
                   :rel    "noopener noreferrer"
                   :class  "font-medium underline hover:text-kleene-secondary-v2"}
               (str "@" login)]]

             connected?
             "Your GitHub account is linked."

             :else
             "Link your GitHub account to get started.")]]]
        [card/card-footer {:class "px-4"}
         (if connected?
           [button {:variant    :error-border
                    :loading     unlink-loading?
                    :disabled   (or loading? unlink-loading?)
                    :on-click   #(dispatch [:vcs.api/unlink-github-user])}
            "Unlink GitHub"]
           [button {:variant    :primary
                    :loading     link-loading?
                    :disabled   (or loading? link-loading?)
                    :on-click   #(dispatch [:vcs.api/link-github-user])}
            "Link GitHub"])]]]]]))
